<!--
  Open canvas -- fallback 3 of card #243 (Artifi mockup fallbacks).

  Where Artifi cannot render a preview, the distributor arranges their
  decorations freely on a garment tinted to the color and saves the result as
  that color's preview image. Each decoration starts where its placement sits
  (the same marks the zone diagram draws); it can be dragged, resized from the
  corner handle, or moved with the keyboard (arrows, Shift for bigger steps,
  + and - to resize).

  VISUAL ONLY: the saved image is what shoppers see for this color. It does
  not move a placement or change any artwork -- production artwork is
  submitted separately. The parent says so on screen.

  The export draws a bundled silhouette, not the supplier photo: see
  utils/openCanvasCompose.js for why (CORS taint).
-->
<template>
    <div class="open-canvas" data-testid="stage-open-canvas">
        <div
            ref="surfaceEl"
            class="open-canvas-surface"
            data-testid="stage-open-canvas-surface"
        >
            <GarmentSilhouette
                :silhouette="silhouette"
                :color="{ name: colorLabel, hex: colorHex }"
                view="front"
                class="open-canvas-garment"
            />
            <div
                v-for="item in items"
                :key="item.key"
                class="open-canvas-item"
                data-testid="stage-open-canvas-item"
                :data-key="item.key"
                :style="boxStyle(item.box)"
                tabindex="0"
                role="button"
                :aria-label="`Move the ${item.label} decoration`"
                @pointerdown="startDrag($event, item, 'move')"
                @keydown="onKey($event, item)"
            >
                <img
                    v-if="item.artworkUrl"
                    :src="item.artworkUrl"
                    crossorigin="anonymous"
                    alt=""
                    class="open-canvas-art"
                    draggable="false"
                />
                <span v-else class="open-canvas-art-fallback" aria-hidden="true">{{
                    item.initial
                }}</span>
                <span
                    class="open-canvas-resize"
                    data-testid="stage-open-canvas-resize"
                    aria-hidden="true"
                    @pointerdown.stop="startDrag($event, item, 'resize')"
                />
            </div>
        </div>
        <p
            v-if="items.length === 0"
            class="open-canvas-empty"
            data-testid="stage-open-canvas-empty"
        >
            Add a placement with artwork to arrange it here.
        </p>
        <div class="open-canvas-actions">
            <button
                type="button"
                class="open-canvas-btn"
                data-testid="stage-open-canvas-reset"
                :disabled="items.length === 0"
                @click="reset"
            >
                Reset
            </button>
            <button
                type="button"
                class="open-canvas-btn open-canvas-btn--primary"
                data-testid="stage-open-canvas-save"
                :disabled="busy || saving || items.length === 0"
                @click="save"
            >
                Save as {{ colorLabel }} preview
            </button>
        </div>
        <p
            v-if="saveError"
            class="open-canvas-error"
            data-testid="stage-open-canvas-error"
            role="alert"
        >
            {{ saveError }}
        </p>
    </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from "vue";
import GarmentSilhouette, {
    isLight,
    silhouetteArtUrl,
} from "@/components/Storefront/GarmentSilhouette.vue";
import { viewBoxFor } from "@/components/decoration/placementPreviewGeometry";
import { composeOpenCanvas } from "@/utils/openCanvasCompose";

const props = defineProps({
    silhouette: { type: String, default: "generic" },
    colorName: { type: String, required: true },
    colorLabel: { type: String, required: true },
    colorHex: { type: String, default: null },
    // [{key, zoneLabel, artworkUrl, initial, box: {leftPct, topPct, wPct, hPct}}]
    marks: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
});
const emit = defineEmits(["save"]);

const MIN_PCT = 3;
const surfaceEl = ref(null);
const items = ref([]);
const saving = ref(false);
const saveError = ref(null);

function fromMarks() {
    return (props.marks ?? []).map((m) => ({
        key: m.key,
        label: m.zoneLabel || "placement",
        artworkUrl: m.artworkUrl || null,
        initial: m.initial || "",
        box: { ...m.box },
    }));
}

function reset() {
    items.value = fromMarks();
}

// A new color or a different set of placements starts from the placements
// again; a re-render with the same ones keeps the distributor's arrangement.
watch(
    () => [props.colorName, (props.marks ?? []).map((m) => m.key).join("|")],
    reset,
    { immediate: true },
);

function clamp(v, lo, hi) {
    return Math.min(Math.max(v, lo), hi);
}

function clampBox(box) {
    const wPct = clamp(box.wPct, MIN_PCT, 100);
    const hPct = clamp(box.hPct, MIN_PCT, 100);
    return {
        leftPct: clamp(box.leftPct, 0, 100 - wPct),
        topPct: clamp(box.topPct, 0, 100 - hPct),
        wPct,
        hPct,
    };
}

function boxStyle(box) {
    return {
        left: `${box.leftPct}%`,
        top: `${box.topPct}%`,
        width: `${box.wPct}%`,
        height: `${box.hPct}%`,
    };
}

let drag = null;

function onPointerMove(ev) {
    if (!drag) return;
    const { rect, start, item, kind, x0, y0 } = drag;
    const dx = ((ev.clientX - x0) / rect.width) * 100;
    const dy = ((ev.clientY - y0) / rect.height) * 100;
    item.box = clampBox(
        kind === "move"
            ? { ...start, leftPct: start.leftPct + dx, topPct: start.topPct + dy }
            : { ...start, wPct: start.wPct + dx, hPct: start.hPct + dy },
    );
}

function endDrag() {
    drag = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endDrag);
    window.removeEventListener("pointercancel", endDrag);
}

function startDrag(ev, item, kind) {
    if (ev.button !== undefined && ev.button !== 0) return;
    const rect = surfaceEl.value?.getBoundingClientRect?.();
    if (!rect || !(rect.width > 0) || !(rect.height > 0)) return;
    ev.preventDefault?.();
    endDrag();
    drag = { rect, start: { ...item.box }, item, kind, x0: ev.clientX, y0: ev.clientY };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
}

onBeforeUnmount(endDrag);

function onKey(ev, item) {
    const step = ev.shiftKey ? 5 : 1;
    const b = item.box;
    const moves = {
        ArrowLeft: { leftPct: b.leftPct - step },
        ArrowRight: { leftPct: b.leftPct + step },
        ArrowUp: { topPct: b.topPct - step },
        ArrowDown: { topPct: b.topPct + step },
    };
    let next = null;
    if (moves[ev.key]) {
        next = { ...b, ...moves[ev.key] };
    } else if (ev.key === "+" || ev.key === "=" || ev.key === "-") {
        const f = ev.key === "-" ? 0.9 : 1.1;
        next = { ...b, wPct: b.wPct * f, hPct: b.hPct * f };
    }
    if (!next) return;
    ev.preventDefault();
    item.box = clampBox(next);
}

async function save() {
    if (saving.value || items.value.length === 0) return;
    saving.value = true;
    saveError.value = null;
    try {
        const blob = await composeOpenCanvas({
            silhouetteUrl: silhouetteArtUrl(props.silhouette),
            viewBox: viewBoxFor(props.silhouette),
            hex: props.colorHex,
            light: isLight(props.colorHex),
            items: items.value.map((i) => ({
                key: i.key,
                artworkUrl: i.artworkUrl,
                initial: i.initial,
                box: { ...i.box },
            })),
        });
        emit("save", blob);
    } catch {
        saveError.value =
            "The canvas could not be saved as an image. Try again, or upload your own image instead.";
    } finally {
        saving.value = false;
    }
}
</script>

<style scoped>
.open-canvas {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    /* No min-height:0 / max-height:100%: the block must keep its own content
       height so the parent scrolls instead of letting the next sibling paint
       over the buttons. */
}
.open-canvas-surface {
    position: relative;
    /* A DEFINITE width, never a share of the parent's height. The Stage gives
       this block no definite height, so a flex-grown surface with
       min-height:0 and only an aspect-ratio collapsed to 0x0 (card #243 test
       failure, 2026-10-21): the garment and artwork were invisible and could
       not be dragged. The height follows from the aspect ratio. The vh term
       keeps the whole block (tabs, canvas, buttons) on a short screen; no
       100% term, because the parent shrink-wraps and a percent of it is
       circular (it measured 221px wide). */
    flex: 0 0 auto;
    width: min(320px, 42vh, 90vw);
    min-width: 160px;
    aspect-ratio: 6 / 7;
    touch-action: none;
    user-select: none;
}
.open-canvas-garment {
    width: 100%;
    height: 100%;
}
.open-canvas-item {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: move;
    outline: 1px dashed var(--tf-border-strong, #9ca3af);
}
.open-canvas-item:focus-visible {
    outline: 2px solid var(--tf-focus, #2563eb);
}
.open-canvas-art {
    width: 100%;
    height: 100%;
    object-fit: contain;
    pointer-events: none;
}
.open-canvas-art-fallback {
    font-size: 11px;
    font-weight: 600;
    color: var(--tf-text-muted, #6b7280);
}
.open-canvas-resize {
    position: absolute;
    right: -5px;
    bottom: -5px;
    width: 10px;
    height: 10px;
    background: var(--tf-focus, #2563eb);
    border-radius: 2px;
    cursor: nwse-resize;
}
.open-canvas-empty,
.open-canvas-error {
    margin: 0;
    font-size: 13px;
}
.open-canvas-error {
    color: var(--tf-text-error, #b91c1c);
}
.open-canvas-actions {
    display: flex;
    gap: 8px;
}
.open-canvas-btn {
    padding: 6px 12px;
    border: 1px solid var(--tf-border, #d1d5db);
    border-radius: 4px;
    background: #fff;
    font-size: 13px;
    cursor: pointer;
}
.open-canvas-btn--primary {
    background: var(--tf-primary, #1f2937);
    border-color: var(--tf-primary, #1f2937);
    color: #fff;
}
.open-canvas-btn:disabled {
    opacity: 0.5;
    cursor: default;
}
</style>
